import { useCallback } from 'react';
import { useParams } from 'react-router';
import {
    DepartmentService,
    type Department,
    type UpdateDepartmentRequest,
} from 'src/api/DepartmentService';
import { invalidateDepartmentData } from '@stores/departmentStore';

type UpdateDepartment = (
    department: UpdateDepartmentRequest,
) => Promise<Department | undefined>;

/*
 * Updates a department (setup: name, config, critical hours, ...) and
 * drops the cached department data on success so every consumer — the
 * sharing tab on the same page, the employees view, the organization
 * home — refetches instead of serving the pre-save department. Returns
 * the updated department, or undefined when the save failed
 * (dynoapeAPI's put swallows HTTP errors and resolves undefined — the
 * axios interceptor has already shown the error toast; the falsy guard
 * below skips the invalidation on failure). Only a missing
 * departmentId throws.
 */
const useUpdateDepartment = (): {
    updateDepartment: UpdateDepartment;
} => {
    const { departmentId } = useParams<{
        departmentId: string;
    }>();

    const updateDepartment = useCallback<UpdateDepartment>(
        async (department: UpdateDepartmentRequest) => {
            if (!departmentId) {
                throw new Error(
                    'useUpdateDepartment requires a departmentId route param',
                );
            }

            const updatedDepartment = await DepartmentService.updateDepartment(
                departmentId,
                department,
            );

            if (!updatedDepartment) {
                return undefined;
            }

            invalidateDepartmentData();

            return updatedDepartment;
        },
        [departmentId],
    );

    return { updateDepartment };
};

export default useUpdateDepartment;
